import React, { FunctionComponent } from 'react';
import {
  Container,
  Grid,
  GridSpacing,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PageFooter, PageHeader } from '../../core/formBuilder/usePages';
import { ImageHeader } from './imageHeader';
import { Footer } from './footer';
import { Theme } from '@mui/material/styles';
import {
  getEnvVariableByKey,
  getTenantIdFromEnv,
} from '../../utils/tenantUtils';
import { useParams } from 'react-router-dom';
import SegmentedLayout from './segments/segmentedLayout';
import { pxOrUndefined } from '../../themes/themeUtils';
import LocalizationSelector from './localization/localizationSelector';

interface ProcessColumnProps {
  pageHeader?: PageHeader;
  pageFooter?: PageFooter;
  disableFormLayout?: boolean; // For hardcoded static pages
  customFormSpacing?: Record<string, number>;
  verticalContentAlignment?: string;
}

export const ProcessColumn: FunctionComponent<ProcessColumnProps> = ({
  pageHeader,
  pageFooter,
  disableFormLayout,
  customFormSpacing,
  verticalContentAlignment,
  children,
}) => {
  const theme = useTheme();
  const { project } = useParams();
  const isPaybackAmexConfig =
    getTenantIdFromEnv() === 'payback' &&
    project?.toLowerCase().includes('amex'); // enable mobile layout on tablets for payback (amex configs)
  const isMobile = useMediaQuery((theme: Theme) =>
    theme?.breakpoints?.down(isPaybackAmexConfig ? 'md' : 'sm')
  );

  const DEFAULT_SEGMENT_HEADER_HEIGHT = '100';
  const DEFAULT_LEGACY_HEADER_HEIGHT = '15';
  const DEFAULT_SEGMENT_FOOTER_HEIGHT = '15';

  const hasHeader =
    !!pageHeader &&
    ((pageHeader.headerMode === 'segments' &&
      (pageHeader.segments?.length ?? 0) > 0) ||
      (pageHeader.headerMode === 'legacy' &&
        !isMobile &&
        !!pageHeader.desktopImage?.identifier) ||
      (isMobile && !!pageHeader.mobileImage?.identifier));

  const getLegacyHeaderHeight = (pageHeader?: PageHeader): number => {
    if (pageHeader?.desktopImage) {
      return parseFloat(
        pageHeader.desktopImage.style?.maxHeight ?? DEFAULT_LEGACY_HEADER_HEIGHT
      );
    }
    if (isMobile && pageHeader?.mobileImage) {
      return parseFloat(
        pageHeader.mobileImage.style?.maxHeight ?? DEFAULT_LEGACY_HEADER_HEIGHT
      );
    }
    return 0;
  };

  const getHeaderHeight = (pageHeader?: PageHeader): number => {
    if (!pageHeader) return 0;
    if (pageHeader.headerMode === 'segments') {
      return parseFloat(pageHeader.height ?? DEFAULT_SEGMENT_HEADER_HEIGHT);
    }
    return getLegacyHeaderHeight(pageHeader);
  };

  const headerHeight = getHeaderHeight(pageHeader);
  const footerHeight = parseFloat(
    pageFooter?.height ?? DEFAULT_SEGMENT_FOOTER_HEIGHT
  );

  const isSegmentedHeader =
    pageHeader?.headerMode === 'segments' && !!pageHeader?.segments;

  // mirrors the legacy image branch rendered below: only an actually displayed
  // desktop/mobile header image gets the optional top spacing, never a segmented header
  const rendersLegacyHeaderImage =
    !isSegmentedHeader &&
    ((!isMobile && !!pageHeader?.desktopImage?.identifier) ||
      (isMobile && !!pageHeader?.mobileImage?.identifier));

  // padding instead of margin so the spacing cannot collapse through <header>
  const headerTopSpacing =
    rendersLegacyHeaderImage && pageHeader?.topSpacingEnabled
      ? theme.spacing(1)
      : undefined;
  const headerTopSpacingPx = headerTopSpacing
    ? parseFloat(headerTopSpacing)
    : 0;

  const basicStyle = {
    marginTop: '2px',
    marginBottom: '2px',
  };

  const isPageFooterPresent = pageFooter?.segments?.some(
    (segment) => segment.type !== 'empty'
  );

  const totalVerticalFooterPaddings =
    (customFormSpacing?.top ?? 0) + (customFormSpacing?.bottom ?? 0);
  // add " - Xpx" only if vertical paddings > 0
  const verticalPaddingPart = totalVerticalFooterPaddings
    ? ` - ${totalVerticalFooterPaddings}px`
    : '';
  // the optional header top spacing adds to the page height and has to be subtracted
  // here, otherwise it causes vertical overflow and shifts the footer out of view
  const headerSpacingPart = headerTopSpacingPx
    ? ` - ${headerTopSpacingPx}px`
    : '';
  const mainContentHeight =
    isPageFooterPresent && pageFooter?.height
      ? `calc(100vh - ${
          headerHeight + footerHeight
        }vh - 4px${verticalPaddingPart}${headerSpacingPart})` // 4px = 2px margin top + 2px margin bottom
      : `calc(100vh - ${headerHeight}vh${headerSpacingPart})`;

  const withFooterStyle = {
    ...basicStyle,
    minHeight: mainContentHeight,
  };

  return (
    <>
      <header
        style={headerTopSpacing ? { paddingTop: headerTopSpacing } : undefined}
      >
        {pageHeader?.headerMode === 'segments' && pageHeader?.segments ? (
          <SegmentedLayout
            segments={pageHeader?.segments ?? []}
            isMobile={isMobile}
            height={pageHeader?.height ?? DEFAULT_LEGACY_HEADER_HEIGHT}
          />
        ) : (
          <>
            {!isMobile && pageHeader?.desktopImage ? (
              <ImageHeader
                image={pageHeader.desktopImage}
                imageTextOverlay={pageHeader.imageTextOverlay}
              />
            ) : isMobile && pageHeader?.mobileImage ? (
              <ImageHeader
                image={pageHeader.mobileImage}
                imageTextOverlay={
                  pageHeader.imageTextOverlay?.showOnMobile
                    ? pageHeader.imageTextOverlay
                    : undefined
                }
              />
            ) : null}
          </>
        )}
        {getEnvVariableByKey('REACT_APP_ENABLE_LOCALIZATION') === 'true' && (
          <LocalizationSelector hasHeader={hasHeader} />
        )}
      </header>
      <main>
        {disableFormLayout ? (
          <Grid
            data-form-content // fixed positioned element can find and align to this column
            container
            justifyContent="center"
            style={{
              paddingTop: pxOrUndefined(customFormSpacing?.top),
              paddingRight: pxOrUndefined(customFormSpacing?.right),
              paddingBottom: pxOrUndefined(customFormSpacing?.bottom),
              paddingLeft: pxOrUndefined(customFormSpacing?.left),
              alignContent: verticalContentAlignment,
              minHeight: mainContentHeight,
            }}
          >
            {children}
          </Grid>
        ) : (
          <Container
            data-form-content // fixed positioned element can find and align to this column
            style={{
              ...(isPageFooterPresent ? withFooterStyle : basicStyle),
              alignContent: verticalContentAlignment,
            }}
            sx={{
              paddingBottom: 2,
            }}
          >
            <Grid
              sx={{
                marginTop: '0px',
              }}
              container
              justifyContent="start"
              spacing={theme.spacing() as GridSpacing}
            >
              {children}
            </Grid>
          </Container>
        )}
      </main>
      {pageFooter?.segments?.some((segment) => segment.type !== 'empty') ? (
        <footer>
          <Footer
            footer={pageFooter}
            isMobile={isMobile}
            styles={{
              paddingTop: pxOrUndefined(customFormSpacing?.top),
              paddingRight: pxOrUndefined(customFormSpacing?.right),
              paddingBottom: pxOrUndefined(customFormSpacing?.bottom),
              paddingLeft: pxOrUndefined(customFormSpacing?.left),
            }}
          ></Footer>
        </footer>
      ) : null}
    </>
  );
};
